// OTE office job page — the pure rules (what's next, who may be assigned, the client confirmation email), kept here
// so they can be tested. Every important step itself goes through the ote-actions server function.
import { format, parseISO } from 'date-fns'
import { sgToday } from './service'

export const OPEN_STAGES = ['scheduled', 'confirmed', 'in_progress', 'awaiting_closeout']
export const stageOf = (job) => job?.ote_stage || 'scheduled'
// A timestamp's day in Singapore (YYYY-MM-DD) — the close-out deadline counts by DAY, as the reminder emails.
export const sgDayOf = (iso) => { try { return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' }) } catch (e) { return '' } }
export const fmtDay = (d) => { try { return d ? format(parseISO(String(d).slice(0, 10)), 'd MMM yyyy') : '' } catch (e) { return '' } }
// "0930" / "09:30" → "9.30am"
export function fmtTime(t) {
  const m = String(t || '').replace(':', '').match(/^(\d{1,2})(\d{2})$/)
  if (!m) return ''
  const h = Number(m[1]), mm = m[2]
  return `${h % 12 || 12}.${mm}${h < 12 ? 'am' : 'pm'}`
}
export const toTimeInput = (t) => { const m = String(t || '').replace(':', '').match(/^(\d{1,2})(\d{2})$/); return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '' }

// The OTE contact person comes from the OTE questionnaire (kept on the job, never copied to the client record).
export const otePic = (job) => ({
  name: job?.form_data?.pic_name || job?.clients?.contact_name || '',
  email: String(job?.form_data?.pic_email || job?.clients?.contact_email || '').trim(),
  phone: job?.form_data?.pic_phone || job?.clients?.contact_phone || '',
})

// May be picked as the OTE auditor: an active, approved auditor whose WSH auditor registration is valid on the audit
// day (the server checks the same again when assigning and when signing).
export function eligibleAuditor(a, auditDate) {
  if (!a || a.active === false || a.approval_status !== 'Approved') return false
  const day = auditDate || sgToday()
  return !(a.wsh_auditor_expiry && a.wsh_auditor_expiry < day)
}

export const setupComplete = (job) => !!(job?.audit_date && job?.start_time && String(job?.audit_site_address || '').trim() && job?.auditor_id)

// The one-line "What's next" at the top of the page. deadlinePassed: the client close-out deadline has passed.
export function oteNextStep(job, { deadline } = {}) {
  if (!job) return ''
  if (job.status === 'Cancelled') return job.ote_stage === 'void' ? 'Voided — the endorsement was never sent.' : 'This job was cancelled.'
  const s = stageOf(job)
  if (s === 'scheduled') return setupComplete(job)
    ? 'Click “📤 Send confirmation”, then paste the copied email into your email to the client in Outlook.'
    : 'Click Edit, fill in the job date, time, site and auditor, then Save draft and 📤 Send confirmation.'
  if (s === 'confirmed') return `Confirmation sent${job.plan_sent_date ? ' on ' + fmtDay(job.plan_sent_date) : ''}. Nothing to do — AuditPM reminds the client 7 days before the job.`
  if (s === 'in_progress') return 'Job in progress — the auditor is working on it. Nothing to do.'
  if (s === 'awaiting_closeout') {
    if (deadline && sgToday() >= sgDayOf(deadline)) return 'The client has not sent everything by the deadline — in “Job & Endorsement”, give more time or close as not endorsed.'
    return `Waiting for the client to send the items still needed${deadline ? ' (by ' + fmtDay(sgDayOf(deadline)) + ')' : ''}. Forward what they send to the auditor.`
  }
  if (s === 'signing') return 'The auditor is signing — refresh the page in a minute.'
  if (s === 'awaiting_payment') return 'Signed, but not sent — waiting for payment. Once paid, click “Send endorsement” in “Job & Endorsement”.'
  if (s === 'ready_to_send') return 'Signed and paid — the auditor (or you) sends the endorsement to the client.'
  if (s === 'sending') return 'The endorsement is being sent — refresh the page in a minute.'
  if (s === 'not_endorsed') return 'Not endorsed. Send the not-endorsed notice to the client.'
  if (s === 'completed') return 'Endorsement sent to the client. Done.'
  if (s === 'closed') return 'Closed — not endorsed. Done.'
  if (s === 'void') return 'Voided — the endorsement was never sent.'
  return ''
}

// Client confirmation email (copied to the clipboard; the office sends it from Outlook, as Internal Audit).
export function oteConfirmationEmail({ greet, company, date, time, location, auditor }) {
  const esc = s => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
  const when = [fmtDay(date), fmtTime(time)].filter(Boolean).join(', ')
  const bring = [
    'Your Risk Assessment / Risk Register covering all the work activities for the overtime exemption (latest approved version)',
    'Safe work procedures for these work activities',
    'Records showing that the affected employees have been informed of the risks and control measures (e.g. briefing or training attendance)',
    'Working hours or shift roster for the overtime exemption period',
  ]
  const p = 'margin:0;line-height:1.5', gap = `<p style="${p}">&nbsp;</p>`
  const html = `<div style="font-family:Calibri,'Segoe UI',Arial,sans-serif;font-size:11pt;line-height:1.5;color:#1f1f1f">
<p style="${p}">Dear ${esc(greet)},</p>${gap}
<p style="${p}">Thank you for engaging CCIS for the endorsement of your Risk Register for Overtime Exemption (OTE). Please find the details below:</p>${gap}
<p style="${p}"><b>Date &amp; Time:</b> ${esc(when)}<br><b>Location:</b> ${esc(location)}<br><b>WSH Auditor:</b> ${esc(auditor)}</p>${gap}
<p style="${p}">Please have the following ready for the auditor:</p>
<ul style="margin:4px 0 0 0;padding-left:22px;line-height:1.5">${bring.map(b => `<li>${esc(b)}</li>`).join('')}</ul>${gap}
<p style="${p}">Please kindly note that if our auditor is already on the way to your location on the day of the visit, a $50 transportation/administrative fee will be applied regardless of postponement reason.</p>${gap}
<p style="${p}">Thank you.</p></div>`
  const text = `Dear ${greet},\n\nThank you for engaging CCIS for the endorsement of your Risk Register for Overtime Exemption (OTE). Please find the details below:\n\nDate & Time: ${when}\nLocation: ${location}\nWSH Auditor: ${auditor}\n\nPlease have the following ready for the auditor:\n${bring.map(b => '• ' + b).join('\n')}\n\nPlease kindly note that if our auditor is already on the way to your location on the day of the visit, a $50 transportation/administrative fee will be applied regardless of postponement reason.\n\nThank you.`
  return { subject: `Confirmation of OTE Verification Visit - ${company} - ${fmtDay(date)}`, html, text }
}

// Plain-English names for the server's history entries (ote_events.event).
export const EVENT_LABEL = {
  'auditor assigned': 'Auditor assigned',
  'confirmation email sent': 'Confirmation sent to the client',
  'audit started': 'Job started',
  'job started': 'Job started',
  'close-out deadline reached — office told': 'Client deadline reached — office emailed',
  'close-out reminder sent': 'Reminder sent to the client',
  'stuck send resolved — email had gone': 'Stuck send fixed — the email had gone',
  'stuck send resolved — nothing was sent': 'Stuck send fixed — nothing had been sent',
  'document added': 'Document added',
  'RA version accepted': 'RA version accepted',
  'item still needed from client': 'Item still needed from client',
  'item closed': 'Item closed',
  'outstanding items emailed to client': 'Outstanding items emailed to client',
  'client given more time': 'Client given more time',
  'signed — satisfactory': 'Signed — satisfactory',
  'signed — not endorsed': 'Signed — not endorsed',
  'closed as not endorsed by office': 'Closed as not endorsed (office)',
  'payment received': 'Payment received',
  'released before payment': 'Released before payment',
  'endorsement sent to client': 'Endorsement sent to the client (query@ in CC)',
  'not-endorsed notice sent to client': 'Not-endorsed notice sent to client',
  'reopened': 'Reopened',
  'voided': 'Voided',
  'endorsement re-issue started': 'Endorsement re-issued',
  'download link resent': 'Download link resent',
}
